package top.wcpe.allincore.dsl.ir.canvas

/**
 * draw 单条指令的 IR 模型。
 *
 * 从原 `DrawBlob.Instruction` 顶级化拆出（M11.x 重构）；行为与字段恒定。
 * - 对于定长 opcode，校验 operands.length == [DrawBlobSpec.expectedOperandCount]。
 * - 对于变长 opcode（POLYGON/PATH），operands 末位分别是 color 与 PATH 的 width+color；
 *   顶点对占据前 2N 位。
 */
class Instruction private constructor(
    // public val：Kotlin 调用方以属性语法 `.opcode` 访问（与原 Java getOpcode() 的合成属性形态一致），
    // 自动生成的 getOpcode() 同时保住 Java 调用方
    val opcode: Byte,
    operandsIn: Array<Operand>,
    pathClosedIn: Boolean,
    pathBuildKindsIn: ByteArray?,
    pathBuildModeIn: Byte,
) {

    private val operands: Array<Operand>

    /** PATH 专用：是否闭合。其它 opcode 恒为 false。 */
    val isPathClosed: Boolean

    /**
     * v4 B.4：PATH_BUILD 专用 — 节点种类数组（每元素 cmdKind byte），
     * 节点坐标 operand 在 [operands] 中按 kinds 顺序排列。其它 opcode 恒为 null。
     */
    private val pathBuildKindsBacking: ByteArray?

    /** v4 B.4：PATH_BUILD 专用 — 0=stroke, 1=fill_nonzero, 2=fill_evenodd。其它 opcode 恒为 0。 */
    val pathBuildMode: Byte

    init {
        if (DrawBlobSpec.isVariadic(opcode)) {
            DrawBlobSpec.validateVariadicOperands(opcode, operandsIn)
        } else {
            val expected = DrawBlobSpec.expectedOperandCount(opcode)
            if (operandsIn.size != expected) {
                throw IllegalArgumentException(
                    "opcode 0x${(opcode.toInt() and 0xFF).toString(16)} 期望 $expected 个操作数，实际 ${operandsIn.size}",
                )
            }
        }
        for (i in operandsIn.indices) {
            @Suppress("SENSELESS_COMPARISON")
            if (operandsIn[i] == null) throw NullPointerException("operands[$i]")
        }
        this.operands = operandsIn.copyOf()
        this.isPathClosed = opcode == DrawOpcodes.OP_PATH && pathClosedIn
        // PATH_BUILD 专用元数据校验：节点 + mode + 与 operands 数量自洽
        if (opcode == DrawOpcodes.OP_PATH_BUILD) {
            if (pathBuildKindsIn == null || pathBuildKindsIn.isEmpty()) {
                throw IllegalArgumentException("PATH_BUILD 至少包含 1 个节点")
            }
            if (pathBuildKindsIn.size > 0xFF) {
                throw IllegalArgumentException("PATH_BUILD 节点数超过 255: ${pathBuildKindsIn.size}")
            }
            if (pathBuildKindsIn[0] != DrawOpcodes.PATH_NODE_MOVE_TO) {
                throw IllegalArgumentException("PATH_BUILD 首节点必须是 MoveTo")
            }
            var totalNodeOperands = 0
            for (k in pathBuildKindsIn) {
                totalNodeOperands += DrawBlobSpec.pathNodeOperandCount(k)
            }
            // operands = 节点坐标 operands + width + color
            if (operandsIn.size != totalNodeOperands + 2) {
                throw IllegalArgumentException(
                    "PATH_BUILD operands 数量 ${operandsIn.size} 与节点坐标总数 $totalNodeOperands + (width+color) 不一致",
                )
            }
            if (pathBuildModeIn < 0 || pathBuildModeIn > 2) {
                throw IllegalArgumentException(
                    "PATH_BUILD mode 必须为 0/1/2，实际: ${pathBuildModeIn.toInt() and 0xFF}",
                )
            }
            this.pathBuildKindsBacking = pathBuildKindsIn.copyOf()
            this.pathBuildMode = pathBuildModeIn
        } else {
            this.pathBuildKindsBacking = null
            this.pathBuildMode = 0
        }
    }

    /** M11.2 通用构造器：操作数支持 LITERAL / EXPR / STR。 */
    constructor(opcode: Byte, operands: Array<Operand>) : this(opcode, operands, false)

    /** M11.4 通用构造器（支持 PATH 的 closed 标志）。 */
    constructor(opcode: Byte, operands: Array<Operand>, pathClosed: Boolean) :
        this(opcode, operands, pathClosed, null, 0)

    /**
     * v4 B.4 PATH_BUILD 专用构造器。
     *
     * @param opcode         必为 [DrawOpcodes.OP_PATH_BUILD]
     * @param operands       节点坐标 operands（按 pathBuildKinds 顺序展开） + width + color
     * @param pathBuildKinds 节点种类数组，元素值见 PATH_NODE_*；至少 1 项且首项必须为 MoveTo
     * @param pathBuildMode  0/1/2，分别表示 stroke / fill_nonzero / fill_evenodd
     */
    constructor(opcode: Byte, operands: Array<Operand>, pathBuildKinds: ByteArray, pathBuildMode: Byte) :
        this(opcode, operands, false, pathBuildKinds, pathBuildMode)

    /** M11.1 兼容构造器：全部 double 字面量。 */
    constructor(opcode: Byte, literals: DoubleArray) :
        this(opcode, toLiteralOperands(opcode, literals), false)

    /** 返回操作数数组拷贝（属性形态，Kotlin 调用方 `.operandsArray`；Java 侧仍为 getOperandsArray()）。 */
    val operandsArray: Array<Operand>
        get() = operands.copyOf()

    /** 包内访问：返回内部数组引用，避免编解码热路径的拷贝开销。调用方禁止修改。 */
    internal fun operandsRaw(): Array<Operand> = operands

    /**
     * v4 B.4：PATH_BUILD 指令的节点种类数组拷贝；其它 opcode 返回 null。
     * 属性形态（Kotlin `.pathBuildKinds`）；Java 侧仍为 getPathBuildKinds()。
     *
     * 调用方据此遍历 operands：每个节点按 [DrawBlobSpec.pathNodeOperandCount] 决定坐标 operand 数。
     */
    val pathBuildKinds: ByteArray?
        get() = pathBuildKindsBacking?.copyOf()

    /** 包内访问：返回内部 kinds 引用，避免编解码热路径的拷贝开销。调用方禁止修改。 */
    internal fun pathBuildKindsRaw(): ByteArray? = pathBuildKindsBacking

    /**
     * 兼容旧 API：仅当所有操作数均为 LITERAL 时返回 double 数组；
     * 含 EXPR 操作数时抛 [IllegalStateException]。
     */
    fun getOperands(): DoubleArray {
        val out = DoubleArray(operands.size)
        for (i in operands.indices) {
            val o = operands[i]
            if (!o.isLiteral()) {
                throw IllegalStateException("操作数 $i 非 LITERAL，无法以 double[] 形式获取")
            }
            out[i] = (o as Operand.Literal).value
        }
        return out
    }

    override fun equals(other: Any?): Boolean {
        if (this === other) return true
        if (other !is Instruction) return false
        return opcode == other.opcode &&
            isPathClosed == other.isPathClosed &&
            pathBuildMode == other.pathBuildMode &&
            operands.contentEquals(other.operands) &&
            pathBuildKindsBacking.contentEquals(other.pathBuildKindsBacking)
    }

    override fun hashCode(): Int {
        var h = 31 * opcode.hashCode() + operands.contentHashCode()
        h = 31 * h + isPathClosed.hashCode()
        h = 31 * h + pathBuildKindsBacking.contentHashCode()
        h = 31 * h + pathBuildMode.hashCode()
        return h
    }

    private companion object {
        private fun toLiteralOperands(opcode: Byte, literals: DoubleArray): Array<Operand> {
            if (DrawBlobSpec.isVariadic(opcode)) {
                throw IllegalArgumentException(
                    "opcode 0x${(opcode.toInt() and 0xFF).toString(16)} 为变长指令，请使用 Operand[] 构造器",
                )
            }
            val expected = DrawBlobSpec.expectedOperandCount(opcode)
            if (literals.size != expected) {
                throw IllegalArgumentException(
                    "opcode 0x${(opcode.toInt() and 0xFF).toString(16)} 期望 $expected 个操作数，实际 ${literals.size}",
                )
            }
            return Array(literals.size) { Operand.literal(literals[it]) }
        }
    }
}
